import React, { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, Button, Box, Alert, Link } from '@mui/material';
import apiClient, { getErrorMessage } from '../../utils/axios';
import AuthCard, { authFieldSx, authLinkSx } from '../../components/AuthCard';
import { sanitizeReturnUrl, goToReturnUrl } from '../../utils/returnUrl';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = sanitizeReturnUrl(searchParams.get('returnUrl'));
  const [email, setEmail] = useState(location.state?.email || searchParams.get('email') || '');
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');
  const [sentMessage, setSentMessage] = useState('');
  const [loading, setLoading] = useState(false);

  /** 에이전트에서 왔으면 에이전트 로그인으로, 아니면 이 사이트 로그인으로 */
  const goLogin = () => {
    if (returnUrl) goToReturnUrl(returnUrl, email);
    else navigate('/login', { state: { email } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) return setEmailError('이메일을 입력하세요.');
    if (!EMAIL_RE.test(email)) return setEmailError('유효한 이메일 주소를 입력하세요.');

    setLoading(true);
    try {
      const res = await apiClient.post('/auth/forgot-password', { email, returnUrl: returnUrl || undefined });
      setSentMessage(res.data?.message || '비밀번호 재설정 메일을 발송했습니다.');
    } catch (err) {
      setError(getErrorMessage(err, '요청 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title="PlanGo 비밀번호 찾기"
      subtitle="가입하신 이메일을 입력하시면 비밀번호를 다시 설정할 수 있는 링크를 보내드립니다."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {sentMessage ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            {sentMessage}
          </Alert>
          <Alert severity="info" sx={{ mb: 3 }}>
            메일이 보이지 않으면 스팸함을 확인해주세요. 링크는 30분 동안 한 번만 사용할 수 있습니다.
          </Alert>
          <Button fullWidth variant="contained" onClick={goLogin} sx={{ py: 1.5, borderRadius: '4px' }}>
            로그인 화면으로
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Link component="button" type="button" onClick={() => setSentMessage('')} sx={authLinkSx}>
              메일을 받지 못하셨나요? 다시 요청
            </Link>
          </Box>
        </>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            id="email"
            placeholder="이메일"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError('');
            }}
            error={!!emailError}
            helperText={emailError}
            sx={authFieldSx}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading || !!emailError}
            sx={{ mt: 1, mb: 0.5, py: 1.5, borderRadius: '4px' }}
          >
            {loading ? '발송 중...' : '재설정 메일 받기'}
          </Button>
        </Box>
      )}
    </AuthCard>
  );
};

export default ForgotPassword;
